import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getCatalogUrl } from "../../../lib/slug";
import { useTranslatedText } from "../../../hooks/useTranslated";
import { useTranslation } from "react-i18next";
import { Product } from "../../../mocks/products";

interface Props {
  product: Product;
}

const isVideoUrl = (url: string) => {
  const ext = url.split("?")[0].split(".").pop()?.toLowerCase() ?? "";
  return ["mp4", "webm", "mov", "avi", "mkv"].includes(ext);
};

const ProductHero = ({ product }: Props) => {
  const { i18n } = useTranslation();
  const isKr = i18n.language === "kr";
  const [loaded, setLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [heroError, setHeroError] = useState(false);

  const displayName = useTranslatedText(product.name, product.nameKr, isKr);
  const displayHeroTagline = useTranslatedText(
    product.heroTagline ?? "",
    product.heroTaglineKr,
    isKr
  );

  const rawMediaItems = (product as unknown as Record<string, unknown>).media_items;
  const mediaItems: Array<{ url: string; type: string; is_cover?: boolean }> = Array.isArray(rawMediaItems)
    ? rawMediaItems as Array<{ url: string; type: string; is_cover?: boolean }>
    : [];

  const videoItem = mediaItems.find((m) => m.type === "video" || isVideoUrl(m.url));
  const coverItem = mediaItems.find((m) => m.is_cover && m.type === "image");
  const firstImgItem = mediaItems.find((m) => m.type === "image");
  const heroMedia = videoItem ?? coverItem ?? firstImgItem ?? (product.images[0] ? { url: product.images[0], type: "image" } : null);
  const heroUrl = heroMedia?.url ?? "";
  const heroIsVideo = heroMedia ? (heroMedia.type === "video" || isVideoUrl(heroUrl)) : false;

  useEffect(() => {
    setHeroError(false);
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, [heroUrl]);

  return (
    <div
      className="relative w-full bg-stone-900"
      style={{ minHeight: "520px" }}
    >
      {/* ── Background media — absolute fill ── */}
      <div className="absolute inset-0">
        {heroIsVideo ? (
          <div className="relative w-full h-full">
            <video
              src={heroUrl}
              className="w-full h-full object-cover opacity-80"
              autoPlay
              loop
              muted={isMuted}
              playsInline
              preload="auto"
              id="hero-video"
              style={{ display: "block" }}
            />
            <button
              onClick={() => {
                setIsMuted((m) => {
                  const vid = document.getElementById("hero-video") as HTMLVideoElement | null;
                  if (vid) vid.muted = !m;
                  return !m;
                });
              }}
              className="absolute bottom-4 right-4 z-20 flex items-center gap-1.5 bg-black/50 hover:bg-black/70 text-white text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer transition-all backdrop-blur-sm"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={isMuted ? "ri-volume-mute-fill" : "ri-volume-up-fill"} />
              </div>
              {isMuted ? "Bật tiếng" : "Tắt tiếng"}
            </button>
          </div>
        ) : (
          <div className="relative w-full h-full flex items-center justify-center bg-[#1a1a2e]">
            {heroError || !heroUrl ? (
              <div className="flex flex-col items-center justify-center gap-3">
                <i className="ri-image-line text-6xl text-white/30" />
                <p className="text-white/50 text-sm font-medium">{product.name}</p>
              </div>
            ) : (
              <img
                src={heroUrl}
                alt={product.name}
                className="w-full h-full object-cover"
                loading="eager"
                decoding="sync"
                style={{ display: "block", imageRendering: "-webkit-optimize-contrast" }}
                onLoad={() => {
                  // eslint-disable-next-line no-console
                  console.debug("[ProductHero] image loaded:", heroUrl);
                }}
                onError={() => {
                  // eslint-disable-next-line no-console
                  console.error("[ProductHero] image failed to load:", heroUrl);
                  setHeroError(true);
                }}
              />
            )}
          </div>
        )}
      </div>

      {/* ── Black overlay — above bg, below all text ── */}
      <div className="absolute inset-0 bg-black/45 z-[5] pointer-events-none" />

      {/* ── Main content — single flex-col flow, no overlap ── */}
      <div className="relative z-10 w-full flex flex-col items-center text-center px-4 md:px-8 pt-16 md:pt-20 pb-12 md:pb-16">
        <div className="max-w-4xl w-full mx-auto flex flex-col items-center">

          {/* Breadcrumb */}
          <nav
            className={`flex items-center gap-1 md:gap-1.5 text-white/60 text-xs md:text-sm flex-wrap justify-center mb-4 md:mb-5 transition-all duration-500 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3"
            }`}
          >
            <Link to="/" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">Trang Chủ</Link>
            <i className="ri-arrow-right-s-line text-white/40" />
            <Link to={`/danh-muc/${product.categoryId}`} className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">
              {product.categoryName}
            </Link>
            {product.subCategoryId && product.subCategoryName && (
              <>
                <i className="ri-arrow-right-s-line text-white/40" />
                <Link to={`/danh-muc/${product.categoryId}?sub=${product.subCategoryId}`} className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                  {product.subCategoryName}
                </Link>
              </>
            )}
            <i className="ri-arrow-right-s-line text-white/40" />
            <span className="text-white/90 max-w-[160px] md:max-w-none truncate">{product.name}</span>
          </nav>

          {/* Category & company label */}
          <div
            className={`flex items-center justify-center gap-2 md:gap-3 mb-4 md:mb-5 flex-wrap transition-all duration-700 delay-100 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <span className="text-[10px] md:text-xs font-bold tracking-[0.15em] md:tracking-[0.2em] text-white/70 uppercase border border-white/30 px-3 md:px-4 py-1 md:py-1.5 rounded-full whitespace-nowrap">
              {product.subCategoryName}
            </span>
            <span className="text-white/50 hidden md:inline">•</span>
            <span className="text-[10px] md:text-xs text-white/60 tracking-widest uppercase whitespace-nowrap">{product.companyName}</span>
          </div>

          {/* Product title */}
          <h1
            className={`text-2xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 md:mb-5 transition-all duration-700 delay-150 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            {displayName}
          </h1>

          {/* English / Korean name */}
          <p
            className={`text-white/50 text-sm md:text-lg mb-6 md:mb-8 transition-all duration-700 delay-200 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            {product.nameKr || product.name}
          </p>

          {/* Tagline */}
          {displayHeroTagline && (
            <p
              className={`hidden sm:block text-white/80 text-sm md:text-base lg:text-lg max-w-xl leading-relaxed mb-6 md:mb-8 transition-all duration-700 delay-300 ${
                loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              {displayHeroTagline}
            </p>
          )}

          {/* Action buttons */}
          <div
            className={`flex items-center justify-center gap-2 md:gap-4 flex-wrap transition-all duration-700 delay-[400ms] ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            {product.originalLink && (
              <a
                href={product.originalLink}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="border border-white/50 text-white px-5 md:px-8 py-2.5 md:py-3.5 rounded-full font-semibold text-xs md:text-sm hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 md:gap-2"
              >
                <i className="ri-external-link-line" />
                <span className="hidden sm:inline">Trang Chủ Doanh Nghiệp</span>
                <span className="sm:hidden">Website</span>
              </a>
            )}
            <Link
              to={getCatalogUrl({ companyName: product.companyName ?? "", name: product.name })}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-white/50 text-white px-5 md:px-8 py-2.5 md:py-3.5 rounded-full font-semibold text-xs md:text-sm hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 md:gap-2"
            >
              <i className="ri-file-list-3-line" />
              <span className="hidden sm:inline">Trang chi tiết sản phẩm riêng</span>
              <span className="sm:hidden">Chi tiết</span>
            </Link>
          </div>

        </div>
      </div>


    </div>
  );
};

export default ProductHero;